import {
  faEnvelope,
  faEye,
  faEyeSlash,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  data,
  json,
  redirect,
  redirectDocument,
} from "@remix-run/node";
import {
  Form,
  useActionData,
  useLoaderData,
  useNavigation,
} from "@remix-run/react";
import React, { useEffect, useRef, useState } from "react";
import StyledLink from "~/components/Users/StyledLink";
import {
  clearedSessionCookie,
  createSession,
  getAccountSession,
  handOffArrivingToken,
} from "~/session.server";
import { resolvePrincipal } from "~/auth/principal.server";
import { safeRedirect } from "~/utils";
import ButtonSpinner from "~/components/ButtonSpinner";
import { ic } from "~/injectionContainer";
import { assertRight } from "~/clean_arch/shared/core/functional_failure";
import { clientAddressOf } from "~/clean_arch/shared/utils/clientAddress";
import { isCrossSitePost } from "~/clean_arch/shared/utils/crossSite";
import { sessionAfterRedemption } from "~/clean_arch/features/account/domain/sessionAfterRedemption";
import { loginView } from "~/clean_arch/features/account/presentation/loginView";

/**
 * These are the form input keys, so they don't accidentally get out of sync.
 */
enum FormFields {
  email = "email",
  password = "password",
}

type ErrorResponse = {
  [keys in FormFields | "server"]?: string;
};

type ActionData = {
  errors: ErrorResponse;
};

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await getAccountSession(request);
  const userToken = session.data.userToken;
  const url = new URL(request.url);
  const redirectTo = safeRedirect(
    url.searchParams.get("redirectTo"),
    "/events"
  );
  const reloadDocument = redirectTo.startsWith("/oauth/");

  // The link is not redeemed on arrival: a mail scanner fetches links before the
  // person clicks, and a single-use link spent that way would refuse the person.
  // The page shows a continue step whose post redeems it. A link carrying the
  // session token itself, as the older links do, is not a link to continue with.
  const handoff = await handOffArrivingToken(
    request,
    "linkToken",
    `/login?redirectTo=${encodeURIComponent(redirectTo)}`,
    userToken
  );
  if (handoff.kind === "redirect") return handoff.response;
  if (handoff.kind === "token") {
    return data(
      { redirectTo, linkToken: handoff.token },
      { headers: handoff.headers }
    );
  }

  // Someone the account service stands behind goes on; a session it no longer
  // stands behind is cleared here, with the form, instead of bouncing through
  // a staff page that would sign it out.
  const principal = await resolvePrincipal(request);
  if (principal.kind === "account") {
    return reloadDocument ? redirectDocument(redirectTo) : redirect(redirectTo);
  }
  const page =
    url.searchParams.get("reset") === "done"
      ? // The reset page sends the person here once the new password is set.
        { redirectTo, passwordReset: true }
      : { redirectTo };
  if (principal.kind === "signedOut") {
    return data(page, {
      headers: { "Set-Cookie": await clearedSessionCookie(request) },
    });
  }
  return page;
}

export async function action({ request }: ActionFunctionArgs) {
  if (isCrossSitePost(request.headers)) {
    throw json(
      { message: "Sign-in must start from this site" },
      { status: 403 }
    );
  }
  const formData = await request.formData();
  const linkToken = formData.get("token")?.toString();
  if (linkToken) return continueWithLink(request, linkToken, formData);
  return signInWithPassword(request, formData);
}

async function continueWithLink(
  request: Request,
  linkToken: string,
  formData: FormData
) {
  const session = await getAccountSession(request);
  const redirectTo = safeRedirect(formData.get("redirectTo"), "/events");
  const reloadDocument = redirectTo.startsWith("/oauth/");
  const outcome = assertRight(
    await ic.redeemLoginLink.execute({
      token: linkToken,
      clientAddress: clientAddressOf(request.headers),
    })
  );
  const decision = sessionAfterRedemption(
    outcome,
    linkToken,
    session.data.userToken
  );
  switch (decision.kind) {
    case "store":
      return createSession({
        request,
        token: decision.token,
        remember: false,
        redirectTo,
        reloadDocument,
      });
    case "keep":
      return reloadDocument
        ? redirectDocument(redirectTo)
        : redirect(redirectTo);
    case "expired":
      return { linkExpired: true };
  }
}

async function signInWithPassword(request: Request, formData: FormData) {
  const email = formData.get(FormFields.email)?.toString();
  const password = formData.get(FormFields.password)?.toString();
  const redirectTo = safeRedirect(formData.get("redirectTo"), "/events");
  const reloadDocument = redirectTo.startsWith("/oauth/");

  // Ensure form fields are not empty
  if (email == null || email == "" || password == null || password == "") {
    const fieldErrors: ErrorResponse = {};
    if (email == null || email == "") {
      fieldErrors.email = "Please enter your email";
    }
    if (password == null || password == "") {
      fieldErrors.password = "Please enter your password";
    }
    return {
      errors: fieldErrors,
    };
  }

  const outcome = assertRight(
    await ic.signInWithPassword.execute({
      email,
      password,
      clientAddress: clientAddressOf(request.headers),
    })
  );
  switch (outcome.outcome) {
    case "session":
      return createSession({
        request,
        token: outcome.sessionToken,
        remember: false,
        redirectTo,
        reloadDocument,
      });
    case "refused":
      // The service does not say which of the two was wrong, so neither field is singled out.
      return { errors: { server: REFUSED } };
    case "throttled":
      return { errors: { server: THROTTLED } };
  }
}

const REFUSED = "Invalid email or password";
const THROTTLED = "Too many attempts for now. Please try again later.";

function LoginPage() {
  const data = useLoaderData<typeof loader>();
  const actionData = useActionData<{ linkExpired?: boolean }>();
  const shown = loginView(data, actionData);
  switch (shown.view) {
    case "continue":
      return (
        <ContinueWithLink
          redirectTo={data.redirectTo}
          linkToken={shown.linkToken}
        />
      );
    case "expired":
      return <ContinueWithLink redirectTo={data.redirectTo} expired />;
    case "form":
      return <LoginForm />;
  }
}

/**
 * The continue step for an emailed link. The link itself is spent only by this
 * form's post, so a mail scanner that fetched the link ahead of the person did
 * not use it up.
 */
function ContinueWithLink({
  redirectTo,
  linkToken,
  expired = false,
}: {
  redirectTo: string;
  /** Absent once the link is known to be spent; there is nothing left to post. */
  linkToken?: string;
  expired?: boolean;
}) {
  const navigation = useNavigation();
  const isLoading = navigation.state !== "idle";

  return (
    <div className="bg-[#C5C4C4]">
      <div className="flex h-screen w-screen items-center justify-center p-5 sm:p-0 ">
        <div className="relative flex w-full max-w-md flex-col items-center space-y-3 rounded bg-white px-10 pb-20 pt-5 shadow-md">
          <img src="/image/Logo.svg" alt="logo" className="h-fit w-fit" />
          <h1 className="text-2xl font-medium">Sign in</h1>
          {expired ? (
            <p
              className="text-center text-sm text-red-700"
              id="link-error"
              role="alert"
            >
              This sign-in link has expired or was already used.{" "}
              <StyledLink to="/login">Sign in another way</StyledLink>
            </p>
          ) : (
            <p className="text-center text-sm text-gray-600">
              Your sign-in link is ready. Continue to open your account.
            </p>
          )}
          {linkToken && (
            <Form
              method="POST"
              replace
              className="flex w-full flex-col items-center"
            >
              <input type="hidden" name="redirectTo" value={redirectTo} />
              <input type="hidden" name="token" value={linkToken} />
              <div className="absolute bottom-0 flex h-12 w-full items-center justify-center rounded-b bg-[#F5F5F5]">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-2/5 rounded bg-[#0E437F] py-2 font-medium text-white transition-all duration-200 disabled:opacity-50"
                >
                  {isLoading && <ButtonSpinner />}
                  {isLoading ? "Please wait..." : "Continue"}
                </button>
              </div>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
}

function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const data = useLoaderData<typeof loader>();
  const redirectTo = data.redirectTo;
  const passwordReset = "passwordReset" in data && data.passwordReset;

  const navigation = useNavigation();
  const isLoading =
    navigation.state == "submitting" || navigation.state === "loading";
  const loginText =
    navigation.state === "submitting"
      ? "Please wait..."
      : navigation.state === "loading"
      ? "Success!"
      : "Login";
  const actionData = useActionData<ActionData>();

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (actionData?.errors?.email) {
      emailRef.current?.focus();
    } else if (actionData?.errors?.password) {
      passwordRef.current?.focus();
    }
  }, [actionData]);

  return (
    <div className="bg-[#C5C4C4]">
      <div className="flex h-screen w-screen items-center justify-center p-5 sm:p-0 ">
        <div className="relative flex w-full max-w-md flex-col items-center space-y-3 rounded bg-white px-10 pb-20 pt-5 shadow-md">
          {/* Logo */}
          <img src="/image/Logo.svg" alt="logo" className="h-fit w-fit" />

          {/* Login */}
          <h1 className="text-2xl font-medium">Login</h1>
          {passwordReset && (
            <p className="text-center text-sm text-green-700" role="status">
              Your password has been reset. Sign in with your new password.
            </p>
          )}

          <Form
            method="POST"
            className="flex w-full flex-col items-center space-y-3"
          >
            <input type="hidden" name="redirectTo" value={redirectTo} />
            {/* Email Input */}
            <div className="w-full">
              <div className="relative flex w-full items-center">
                <div className="absolute left-0 flex h-full items-center border-r px-4 text-[#DDDDDD]">
                  <FontAwesomeIcon icon={faEnvelope} />
                </div>
                <input
                  type={FormFields.email}
                  name={FormFields.email}
                  ref={emailRef}
                  required
                  className={`w-full rounded-md border px-4 py-2 pl-14 outline-none placeholder:text-[#DDDDDD] ${
                    actionData?.errors?.email
                      ? "focus:border-red-400 focus:shadow-[0_0_5px_0_rgba(185,28,28,1)]"
                      : "focus:border-blue-400 focus:shadow-[0_0_5px_0_rgba(96,165,250,1)]"
                  }`}
                  placeholder="Email"
                />
              </div>

              {/* Tooltip to show error */}
              {actionData?.errors?.email && (
                <div
                  className="w-full text-left text-sm text-red-700"
                  id="email-error"
                >
                  {actionData.errors.email}
                </div>
              )}
            </div>

            {/* Password Input */}
            <div className="w-full">
              <div className="relative flex w-full items-center">
                <div className="absolute left-0 flex h-full items-center border-r px-4 text-[#DDDDDD]">
                  <FontAwesomeIcon icon={faLock} />
                </div>
                <input
                  type={showPassword ? "text" : FormFields.password}
                  name={FormFields.password}
                  ref={passwordRef}
                  required
                  className={`w-full rounded-md border px-4 py-2 pl-14 outline-none placeholder:text-[#DDDDDD] ${
                    actionData?.errors?.password
                      ? "focus:border-red-400 focus:shadow-[0_0_5px_0_rgba(185,28,28,1)]"
                      : "focus:border-blue-400 focus:shadow-[0_0_5px_0_rgba(96,165,250,1)]"
                  }`}
                  placeholder="Password"
                />
                <div className="absolute right-0 flex h-full items-center border-r px-4 text-[#DDDDDD]">
                  <FontAwesomeIcon
                    onClick={() => setShowPassword(!showPassword)}
                    icon={showPassword ? faEye : faEyeSlash}
                    className="h-5 w-5 cursor-pointer"
                  />
                </div>
              </div>

              {/* Tooltip to show error */}
              {actionData?.errors?.password && (
                <div
                  className="w-full text-left text-sm text-red-700"
                  id="password-error"
                >
                  {actionData.errors.password}
                </div>
              )}
            </div>

            {/* Tooltip to show error */}
            {actionData?.errors?.server && (
              <div
                className="w-full text-left text-sm text-red-700"
                id="server-error"
                role="alert"
              >
                {actionData.errors.server}
              </div>
            )}

            {/* Forgot Password */}
            <div className="w-full text-right">
              <StyledLink to="/forgot-password">Forgot Password?</StyledLink>
            </div>

            {/* Login Button */}
            <div className="absolute bottom-0 flex h-12 w-full items-center justify-center rounded-b bg-[#F5F5F5]">
              <button
                type="submit"
                disabled={isLoading}
                className="w-2/5 rounded bg-[#0E437F] py-2 font-medium text-white transition-all duration-200 disabled:opacity-50"
              >
                {isLoading && navigation.state !== "loading" && (
                  <ButtonSpinner />
                )}
                {loginText}
              </button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
